import React from 'react';
import Editor from '@monaco-editor/react';

const FlowChartFunctionWindows = ({
  functionWindows,
  setFunctionWindows,
  draggingWindowId,
  setDraggingWindowId,
  setDragOffset,
  setResizingWindowId,
  setResizeStart,
  setResizeDirection,
  saveFunctionWindow,
  closeFunctionWindow,
  toggleMinimizeWindow,
  functionWindowEditorRefs,
}) => {
  return (
    <>
      {functionWindows.map((win) => {
        const zIndex = 100003 + functionWindows.length - functionWindows.findIndex((w) => w.id === win.id);
        const winWidth = win.isMinimized ? 400 : win.width || 800;
        const winHeight = win.isMinimized ? 40 : win.height || 600;

        return (
          <div
            key={win.id}
            style={{
              position: 'fixed',
              left: Math.max(0, Math.min(win.position.x, window.innerWidth - winWidth)),
              top: Math.max(0, Math.min(win.position.y, window.innerHeight - winHeight)),
              width: winWidth,
              height: winHeight,
              background: '#2d2d2d',
              borderRadius: 8,
              border: '2px solid #3e3e42',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              display: 'flex',
              flexDirection: 'column',
              zIndex: zIndex,
              overflow: 'hidden',
              userSelect: 'none',
            }}
            onClick={() => {
              setFunctionWindows((prev) => {
                const filtered = prev.filter((w) => w.id !== win.id);
                return [...filtered, win];
              });
            }}
          >
            {/* Header - Draggable */}
            <div
              onMouseDown={(e) => {
                const rect = e.currentTarget.closest('div').getBoundingClientRect();
                setDragOffset({
                  x: e.clientX - rect.left,
                  y: e.clientY - rect.top,
                });
                setDraggingWindowId(win.id);
              }}
              style={{
                background: '#1e1e1e',
                borderBottom: '1px solid #3e3e42',
                padding: '8px 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: draggingWindowId === win.id ? 'grabbing' : 'grab',
                flexShrink: 0,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                <h3
                  style={{
                    color: '#cccccc',
                    margin: 0,
                    fontSize: 14,
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  📝 {win.functionName}
                </h3>
                {win.saveStatus && (
                  <span
                    style={{
                      fontSize: 11,
                      padding: '2px 6px',
                      borderRadius: 4,
                      color: win.saveStatus.state === 'error' ? '#ff6b6b' : '#a7f3d0',
                      background: win.saveStatus.state === 'error' ? '#3a1f1f' : '#1f3a2a',
                      border: `1px solid ${win.saveStatus.state === 'error' ? '#5a2a2a' : '#2a5a3a'}`,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {win.saveStatus.message}
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    saveFunctionWindow(win.id);
                  }}
                  style={{
                    background: '#0e639c',
                    border: 'none',
                    color: '#ffffff',
                    padding: '4px 10px',
                    borderRadius: 4,
                    fontSize: 11,
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                  title="Kaydet"
                >
                  💾 Kaydet
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleMinimizeWindow(win.id);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#888',
                    fontSize: 16,
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title={win.isMinimized ? 'Büyüt' : 'Küçült'}
                >
                  {win.isMinimized ? '□' : '−'}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    closeFunctionWindow(win.id);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#888',
                    fontSize: 18,
                    cursor: 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Kapat"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Content - Editor */}
            {!win.isMinimized && (
              <div
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  background: '#1e1e1e',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
              >
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Editor
                    height="100%"
                    width="100%"
                    defaultLanguage="php"
                    value={win.script}
                    theme="vs-dark"
                    onChange={(value) => {
                      setFunctionWindows((prev) =>
                        prev.map((w) => (w.id === win.id ? { ...w, script: value || '' } : w)),
                      );
                    }}
                    onMount={(editor) => {
                      functionWindowEditorRefs.current[win.id] = { current: editor };
                    }}
                    options={{
                      readOnly: false,
                      fontSize: 13,
                      minimap: { enabled: true },
                      wordWrap: 'on',
                      automaticLayout: true,
                      lineNumbers: 'on',
                      roundedSelection: true,
                      cursorStyle: 'line',
                      folding: true,
                      scrollBeyondLastLine: true,
                      renderWhitespace: 'selection',
                      tabSize: 4,
                    }}
                  />
                </div>
              </div>
            )}

            {/* Resize Handles - 4 köşe */}
            {!win.isMinimized && (
              <>
                <div
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setResizingWindowId(win.id);
                    setResizeStart({
                      x: e.clientX,
                      y: e.clientY,
                      width: win.width || 800,
                      height: win.height || 600,
                      startX: win.position.x,
                      startY: win.position.y,
                    });
                    setResizeDirection('nw');
                  }}
                  style={{
                    position: 'absolute',
                    top: -2,
                    left: -2,
                    width: 12,
                    height: 12,
                    cursor: 'nw-resize',
                    zIndex: zIndex + 1,
                    background: 'rgba(139, 92, 246, 0.3)',
                    border: '1px solid rgba(139, 92, 246, 0.6)',
                    borderRadius: '2px',
                  }}
                />
                <div
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setResizingWindowId(win.id);
                    setResizeStart({
                      x: e.clientX,
                      y: e.clientY,
                      width: win.width || 800,
                      height: win.height || 600,
                      startX: win.position.x,
                      startY: win.position.y,
                    });
                    setResizeDirection('ne');
                  }}
                  style={{
                    position: 'absolute',
                    top: -2,
                    right: -2,
                    width: 12,
                    height: 12,
                    cursor: 'ne-resize',
                    zIndex: zIndex + 1,
                    background: 'rgba(139, 92, 246, 0.3)',
                    border: '1px solid rgba(139, 92, 246, 0.6)',
                    borderRadius: '2px',
                  }}
                />
                <div
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setResizingWindowId(win.id);
                    setResizeStart({
                      x: e.clientX,
                      y: e.clientY,
                      width: win.width || 800,
                      height: win.height || 600,
                      startX: win.position.x,
                      startY: win.position.y,
                    });
                    setResizeDirection('sw');
                  }}
                  style={{
                    position: 'absolute',
                    bottom: -2,
                    left: -2,
                    width: 12,
                    height: 12,
                    cursor: 'sw-resize',
                    zIndex: zIndex + 1,
                    background: 'rgba(139, 92, 246, 0.3)',
                    border: '1px solid rgba(139, 92, 246, 0.6)',
                    borderRadius: '2px',
                  }}
                />
                <div
                  onMouseDown={(e) => {
                    e.stopPropagation();
                    setResizingWindowId(win.id);
                    setResizeStart({
                      x: e.clientX,
                      y: e.clientY,
                      width: win.width || 800,
                      height: win.height || 600,
                      startX: win.position.x,
                      startY: win.position.y,
                    });
                    setResizeDirection('se');
                  }}
                  style={{
                    position: 'absolute',
                    bottom: -2,
                    right: -2,
                    width: 12,
                    height: 12,
                    cursor: 'se-resize',
                    zIndex: zIndex + 1,
                    background: 'rgba(139, 92, 246, 0.3)',
                    border: '1px solid rgba(139, 92, 246, 0.6)',
                    borderRadius: '2px',
                  }}
                />
              </>
            )}
          </div>
        );
      })}
    </>
  );
};

export default FlowChartFunctionWindows;
